import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleField, LabelWithHelp, FieldHelp, Button, SecretPasswordField } from '../../../components';
import { Field, useFormikContext } from 'formik';
import AuthApi from '../../../services/Auth';

interface AuthFormProps {};

function AuthForm(props: AuthFormProps) {
    const { t } = useTranslation();
    const { values } = useFormikContext<AnyObj>();
    const [testState, setTestState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
    const [testMessage, setTestMessage] = useState('');

    const provisioningMode = values?.auth?.openid?.provisioning_mode || 'jit';

    const testOpenId = async () => {
        setTestState('loading');
        setTestMessage('');
        try {
            const result = await AuthApi.testOpenId();
            if (result.ok) {
                setTestState('ok');
                setTestMessage(
                    t('settings.openid_test_ok', {
                        issuer: result.issuer || '',
                        callback: result.callback_url || '',
                    }) as string
                );
            } else {
                setTestState('error');
                setTestMessage(result.error || t('settings.openid_test_failed') as string);
            }
        } catch (err: any) {
            setTestState('error');
            setTestMessage(err?.message || t('settings.openid_test_failed') as string);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
            <div className="flex-1">
                <div className="mt-5">
                    <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                        {t('settings.auth_openid')}
                        <FieldHelp content={t('settings.help.auth_modes') || ''} />
                    </h2>
                    <div className="mt-2 flex space-between">
                        <label htmlFor="openid" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_auth_openid')}
                        </label>
                        <ToggleField name="auth.openid.enabled" className="inline"/>
                    </div>
                </div>
                <div className="mt-5">
                    <LabelWithHelp htmlFor="configuration_url" help={t('settings.help.openid_url') || ''}>
                        {t('settings.auth_openid_configuration_url')}
                    </LabelWithHelp>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="auth.openid.configuration_url"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            placeholder="https://sso.example.com/application/o/yoda/.well-known/openid-configuration"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="client_id" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.auth_openid_client_id')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="password"
                            name="auth.openid.client_id"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="client_secret" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.auth_openid_client_secret')}
                    </label>
                    <div className="mt-2">
                        <SecretPasswordField
                            name="auth.openid.client_secret"
                            hasFlagName="auth.openid.hasClientSecret"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <LabelWithHelp htmlFor="provisioning_mode" help={t('settings.help.openid_provisioning') || ''}>
                        {t('settings.auth_openid_provisioning_mode')}
                    </LabelWithHelp>
                    <div className="mt-2">
                        <Field
                            as="select"
                            name="auth.openid.provisioning_mode"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        >
                            <option value="jit">{t('settings.auth_openid_mode_jit')}</option>
                            <option value="invite_only">{t('settings.auth_openid_mode_invite_only')}</option>
                        </Field>
                    </div>
                </div>
                {provisioningMode === 'jit' && (
                    <div className="mt-5">
                        <label htmlFor="jit_default_role" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.auth_openid_jit_default_role')}
                        </label>
                        <div className="mt-2">
                            <Field
                                as="select"
                                name="auth.openid.jit_default_role"
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            >
                                <option value="user">{t('users.role_user')}</option>
                                <option value="external">{t('users.role_external')}</option>
                                <option value="client">{t('users.role_client')}</option>
                            </Field>
                        </div>
                    </div>
                )}
                <div className="mt-5">
                    <Button
                        type="button"
                        color="secondary"
                        className="rounded-sm"
                        loading={testState === 'loading'}
                        title={t('settings.openid_test')}
                        onClick={testOpenId}
                    />
                    {testMessage && (
                        <p className={`mt-2 text-sm ${testState === 'ok' ? 'text-green-700' : 'text-red-700'}`}>
                            {testMessage}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex-1">
                <div className="mt-5">
                    <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                        {t('settings.auth_password')}
                        <FieldHelp content={t('settings.help.auth_modes') || ''} />
                    </h2>
                    <div className="mt-2 flex space-between">
                        <label htmlFor="password" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_auth_password')}
                        </label>
                        <ToggleField name="auth.password.enabled" className="inline"/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AuthForm;
